import { isEmpty } from 'lodash-es';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useSessionstorageState } from 'rooks';
import shallow from 'zustand/shallow';

import useVideoPlayerStore from '../../store';

export const usePlayerEvents = (player, displayControls) => {
  const router = useRouter();
  const {
    contentState,
    currentTime,
    mixpanelTalkDetails,
    playbackMode,
    playbackRate,
    playbackRequested,
    onSubtitleCue,
    onTranscriptToggle,
    subtitleCues,
    service,
    onCurrentTime,
    onContentTime,
    onStreamTime,
    prerollDuration,
    onPlaybackRate
  } = useVideoPlayerStore(
    state => ({
      ...state
    }),
    shallow
  );

  const internalPlayer = service === 'internal';

  const playbackRequestRef = useRef(playbackRequested);

  const [storedPlaybackRate, setStoredPlaybackRate] = useSessionstorageState(
    'playback_rate_state',
    playbackRate
  );

  useEffect(() => {
    if (player) {
      const handleRateChange = () => {
        setStoredPlaybackRate(player.playbackRate);
        onPlaybackRate(player.playbackRate);
      };

      player.addEventListener('ratechange', handleRateChange);

      return () => {
        player.removeEventListener('ratechange', handleRateChange);
      };
    }
  }, [player, onPlaybackRate]);

  useEffect(() => {
    // Check if the player's playbackRate is different from the stored or desired playbackRate
    if (player && currentTime > 0) {
      player.playbackRate = storedPlaybackRate || playbackRate;
    }
  }, [
    player,
    playbackRate,
    playbackMode,
    storedPlaybackRate,
    contentState,
    currentTime
  ]);

  useEffect(() => {
    if (!player) {
      return;
    }

    const handleTimeUpdate = () => {
      onStreamTime(player.currentTime);

      // Adjust current time to account for pre-roll ad duration
      const adjustedCurrentTime = player?.currentTime;

      // Update the current time in the state, ensuring it never goes below 0
      onCurrentTime(Math.max(0, adjustedCurrentTime));

      // Update content time for seeking purposes, converting seconds to milliseconds
      onContentTime(Math.ceil(adjustedCurrentTime) * 1000);
    };

    // Function to update the subtitle cue based on the current time
    const updateSubtitleCue = currentTime => {
      const currentCue = subtitleCues.find(
        cue => currentTime >= cue.startTime && currentTime <= cue.endTime
      );

      if (currentCue) {
        onSubtitleCue(currentCue.text);
      } else {
        onSubtitleCue('');
      }
    };

    // Event listener for time updates
    const timeUpdateHandler = () => {
      if (subtitleCues.length > 0) {
        updateSubtitleCue(currentTime);
      }

      handleTimeUpdate();
    };

    if (playbackMode !== 'ended' && playbackMode !== 'unstarted') {
      player.addEventListener('timeupdate', timeUpdateHandler);
      return () => player.removeEventListener('timeupdate', timeUpdateHandler);
    }
  }, [
    player,
    onSubtitleCue,
    subtitleCues,
    currentTime,
    displayControls,
    internalPlayer,
    playbackMode,
    prerollDuration,
    onStreamTime,
    onCurrentTime,
    onContentTime
  ]);

  useEffect(() => {
    if (
      router.query.slug.includes('transcript') &&
      !isEmpty(mixpanelTalkDetails)
    ) {
      // If the transcript is open, set the transcript toggle to true
      onTranscriptToggle(true);
    }
  }, [mixpanelTalkDetails]);

  useEffect(() => {
    playbackRequestRef.current = playbackRequested;
  }, [playbackRequested]);
};
